import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";

interface PromptEditorModalProps {
  isOpen: boolean;
  prompt: string;
  onClose: () => void;
  onSave: (prompt: string) => void;
}

const parseVariables = (text: string): string[] => {
  const variablePattern = /\{(\w+)\}/g;
  const variables = new Set<string>();
  let match;

  while ((match = variablePattern.exec(text)) !== null) {
    const variable = match[1];
    if (variable) variables.add(variable);
  }

  return Array.from(variables).sort();
};

export const PromptEditorModal: React.FC<PromptEditorModalProps> = ({
  isOpen,
  prompt,
  onClose,
  onSave,
}) => {
  const [editedPrompt, setEditedPrompt] = useState(prompt);
  const [debouncedPrompt, setDebouncedPrompt] = useState(prompt);
  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  const parsedVariables = useMemo(() => parseVariables(editedPrompt), [editedPrompt]);

  // Update local state when prompt prop changes
  useEffect(() => {
    setEditedPrompt(prompt);
    setDebouncedPrompt(prompt);
  }, [prompt]);

  // Debounce preview updates (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPrompt(editedPrompt);
    }, 300);

    return () => clearTimeout(timer);
  }, [editedPrompt]);



  // Focus trapping within modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }

      // Close on Escape key
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Focus textarea when modal opens
    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSave = useCallback(() => {
    onSave(editedPrompt);
    onClose();
  }, [editedPrompt, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-[var(--card)] rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="p-4 border-b border-[var(--border)] flex justify-between items-center flex-shrink-0">
          <h2
            id="modal-title"
            className="text-xl font-semibold text-[var(--foreground)]"
          >
            Edit System Prompt
          </h2>
          <button
            ref={closeButtonRef}
            onClick={handleClose}
            className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content - Side by side on desktop, stacked on mobile */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          {/* Editor Panel */}
          <div className="flex-1 lg:basis-1/2 min-w-0 flex flex-col border-b lg:border-b-0 lg:border-r border-[var(--border)] min-h-[300px] lg:min-h-0">
            <div className="p-3 bg-[var(--muted)] border-b border-[var(--border)] flex-shrink-0">
              <h3 className="text-sm font-medium text-[var(--foreground)]">
                Editor
              </h3>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <textarea
                ref={textareaRef}
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                className="w-full h-full min-h-[400px] lg:min-h-full p-3 bg-[var(--background)] text-[var(--foreground)] border border-[var(--border)] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[rgb(255,204,51)] focus:border-transparent font-mono text-sm overflow-y-auto"
                placeholder="Enter your system prompt here..."
                aria-label="System prompt editor"
              />
            </div>
          </div>

          {/* Preview Panel */}
          <div className="flex-1 lg:basis-1/2 min-w-0 flex flex-col min-h-[300px] lg:min-h-0">
            <div className="p-3 bg-[var(--muted)] border-b border-[var(--border)] flex-shrink-0">
              <h3 className="text-sm font-medium text-[var(--foreground)]">
                Markdown Preview
              </h3>
            </div>
            <div ref={previewRef} className="flex-1 overflow-auto p-4">
              <div className="prose prose-sm max-w-none dark:prose-invert text-[var(--foreground)]">
                <ReactMarkdown
                  components={{
                    // Custom styling for markdown elements
                    h1: ({ node, ...props }) => (
                      <h1 className="text-2xl font-bold mb-4 text-[var(--foreground)]" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="text-xl font-bold mb-3 text-[var(--foreground)]" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="text-lg font-bold mb-2 text-[var(--foreground)]" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-3 text-[var(--foreground)]" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="list-disc list-inside mb-3 text-[var(--foreground)]" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="list-decimal list-inside mb-3 text-[var(--foreground)]" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="mb-1 text-[var(--foreground)]" {...props} />
                    ),
                    code: ({ node, inline, ...props }: any) =>
                      inline ? (
                        <code
                          className="bg-[var(--muted)] px-1.5 py-0.5 rounded text-sm font-mono text-[var(--foreground)]"
                          {...props}
                        />
                      ) : (
                        <code
                          className="block bg-[var(--muted)] p-3 rounded text-sm font-mono overflow-x-auto text-[var(--foreground)]"
                          {...props}
                        />
                      ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote
                        className="border-l-4 border-[var(--border)] pl-4 italic my-3 text-[var(--foreground-muted)]"
                        {...props}
                      />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-[var(--foreground)]" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="italic text-[var(--foreground)]" {...props} />
                    ),
                  }}
                >
                  {debouncedPrompt || "*No content to preview*"}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>

        {/* Variables section */}
        {parsedVariables.length > 0 && (
          <div className="px-4 py-3 border-t border-[var(--border)] bg-[var(--muted)] flex-shrink-0">
            <div className="text-sm font-medium text-[var(--foreground)] mb-2">
              Variables Found:
            </div>
            <div className="flex flex-wrap gap-2">
              {parsedVariables.map((variable) => (
                <span
                  key={variable}
                  className="px-3 py-1 bg-[var(--background)] border border-[var(--border)] rounded-full text-sm text-[var(--foreground)] font-mono"
                >
                  {variable}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer with action buttons */}
        <div className="p-4 border-t border-[var(--border)] flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-[var(--foreground)] border border-[var(--border)] rounded-lg hover:bg-[var(--muted)] transition-colors"
          >
            Close
          </button>
          <button
            ref={saveButtonRef}
            onClick={handleSave}
            className="px-6 py-2 bg-[rgb(255,204,51)] text-black font-medium rounded-lg hover:bg-[rgb(255,204,51)]/90 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
